import { useCallback, useEffect } from 'react';
import { InsuletStatusCode } from 'Enums';
import { ApiLoginWarning } from 'Constants/index';
import { redirect } from 'Utilities/browser';
import mediator from 'Utilities/mediator';
import { getAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { verifyCid } from 'Services/insulet';
import useAuth from '../../useAuth';
import useInsuletError from './useInsuletError';
const useInsuletAuth = () => {
  const {
    redirectOnError
  } = useInsuletError();
  const onDisplayLockoutMessage = useCallback(() => {
    setTimeout(() => {
      const redirectUrl = store.getState().env.redirect_uri;
      redirectOnError(redirectUrl, InsuletStatusCode.LOCKOUT_ERROR);
    }, 5000);
  }, [redirectOnError]);
  const {
    handleLoginError,
    handleLockoutError,
    showHcpError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showTokenExpirationError,
    showCountryRegionError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
    navigateToPhoneVerification,
    authenticateUser,
    hasPendingConsents
  } = useAuth(onDisplayLockoutMessage);
  useEffect(() => {
    const sessionExpiredInterval = setTimeout(() => {
      const redirectUrl = store.getState().env.redirect_uri;
      redirectOnError(redirectUrl, InsuletStatusCode.INTERNAL_ERROR);
    }, 600000);
    return () => {
      clearTimeout(sessionExpiredInterval);
    };
  }, [redirectOnError]);
  const redirectUserAfterAuth = useCallback(async ({
    accessToken,
    accountId,
    cid,
    country,
    redirect_uri
  }) => {
    const redirectUrl = await verifyCid(cid, country, redirect_uri, accountId, accessToken);
    redirect(redirectUrl);
  }, []);
  const onConsentsUpdate = useCallback(() => {
    const authSession = getAuthSession();
    if (authSession) {
      const {
        redirect_uri,
        cid
      } = store.getState().env;
      const {
        accessToken,
        accountId,
        country,
        authOrigin
      } = authSession;
      if (authOrigin === 'register') {
        mediator.publish('router:navigate', '/login');
        return;
      }
      redirectUserAfterAuth({
        cid,
        country,
        redirect_uri,
        accountId,
        accessToken
      });
      return;
    }
    mediator.publish('router:navigate', '/login');
  }, [redirectUserAfterAuth]);
  const authenticateUserAndRedirect = useCallback(async (email, password) => {
    const {
      cid,
      redirect_uri
    } = store.getState().env;
    const {
      verificationTasks,
      accountId,
      accessToken,
      country,
      patient
    } = await authenticateUser(email, password);
    const unverifiedPhoneTask = verificationTasks?.find(task => task.verified === false && task.action === ApiLoginWarning.REGISTER_ACTION && task.verificationMethod === ApiLoginWarning.PHONE_VERIFICATION);
    if (!unverifiedPhoneTask) {
      if (hasPendingConsents(patient)) {
        return;
      }
      redirectUserAfterAuth({
        cid,
        country,
        redirect_uri,
        accountId,
        accessToken
      });
      return;
    }
    return navigateToPhoneVerification(unverifiedPhoneTask, async () => {
      const {
        accessToken,
        accountId,
        patient
      } = await authenticateUser(email, password);
      if (hasPendingConsents(patient)) {
        return;
      }
      redirectUserAfterAuth({
        cid,
        country,
        redirect_uri,
        accountId,
        accessToken
      });
    });
  }, [authenticateUser, hasPendingConsents, navigateToPhoneVerification, redirectUserAfterAuth]);
  const handleCountryRegionError = useCallback(() => {
    const redirectUrl = store.getState().env.redirect_uri;
    redirectOnError(redirectUrl, InsuletStatusCode.WRONG_REGION);
  }, [redirectOnError]);
  const handleForgotPasswordDone = useCallback(() => {
    const redirectUrl = store.getState().env.redirect_uri;
    redirectOnError(redirectUrl, InsuletStatusCode.FORGOT_PASSWORD);
  }, [redirectOnError]);
  const handleResetPasswordDone = useCallback(() => {
    const partners = store.getState().config.partners;
    const pId = store.getState().env.pId;
    const website = partners ? partners[pId]?.website : '';
    if (website) {
      redirect(website);
    }
  }, []);
  const handleUnexpectedError = useCallback(() => {
    mediator.publish('router:navigate', '/auth-error');
    setTimeout(() => {
      const redirectUrl = store.getState().env.redirect_uri;
      redirectOnError(redirectUrl, InsuletStatusCode.INTERNAL_ERROR);
    }, 5000);
  }, [redirectOnError]);
  return {
    authenticateUserAndRedirect,
    authenticateUser,
    onConsentsUpdate,
    handleLoginError,
    handleLockoutError,
    handleCountryRegionError,
    handleForgotPasswordDone,
    handleResetPasswordDone,
    handleUnexpectedError,
    showHcpError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showTokenExpirationError,
    showCountryRegionError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage
  };
};
export default useInsuletAuth;